/**
 * WpBodyEditor — three-mode safe editor for WordPress page / post bodies.
 *
 *   Visual (default)
 *     - TipTap WYSIWYG. Users get bold / italic / lists / links / headings
 *       without touching HTML. On save it serializes back to HTML.
 *     - Shortcodes ([…]) and Gutenberg block markers (<!-- wp:… -->) are
 *       preserved verbatim — TipTap stores them as raw text nodes that
 *       round-trip through HTML safely.
 *
 *   Source
 *     - Raw HTML / shortcode textarea. For power users / Tech Specialist.
 *
 *   Read
 *     - Plain-text reading view, strip-tags, no save. Prevents the
 *       single biggest risk of a "plain text editor" — silently
 *       destroying shortcodes & formatting on save.
 *
 * The component is fully controlled. Parent owns the `value` (HTML
 * string) and `onChange` setter. Mode toggling is internal.
 */
import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';

const MODES = [
  { key: 'visual', label: 'Visual',
    help: 'Format with toolbar buttons. Shortcodes and block comments are preserved.' },
  { key: 'source', label: 'Source (HTML)',
    help: 'Raw HTML and shortcodes. For power users.' },
  { key: 'read',   label: 'Read view',
    help: 'Plain-text reading. No editing — protects you from accidentally wiping formatting.' },
];

function stripHtml(html) {
  if (!html) return '';
  // Strip block-comment markers and HTML tags, decode common entities.
  return html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default function WpBodyEditor({ value, onChange, testIdPrefix = 'wp-body' }) {
  const [mode, setMode] = useState('visual');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Re-load the editor content when the parent value changes (row swap).
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  // Switching INTO visual mode after a source-edit: re-feed the textarea
  // value into the TipTap instance so the WYSIWYG reflects the latest.
  useEffect(() => {
    if (mode === 'visual' && editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const activeMode = MODES.find((m) => m.key === mode);

  return (
    <div data-testid={`${testIdPrefix}-editor`} className="space-y-2">
      {/* Mode tabs */}
      <div className="flex items-center gap-1 border-b">
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            data-testid={`${testIdPrefix}-mode-${m.key}`}
            onClick={() => setMode(m.key)}
            className={`px-3 py-1.5 text-xs border-b-2 ${
              mode === m.key
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >{m.label}</button>
        ))}
        <span className="ml-3 text-xs text-gray-400">{activeMode?.help}</span>
      </div>

      {/* Visual mode toolbar — only shown when relevant */}
      {mode === 'visual' && editor && (
        <div className="flex flex-wrap items-center gap-1 px-2 py-1 bg-gray-50 border rounded-t text-xs">
          <ToolbarBtn editor={editor} cmd="toggleBold" label="B" active="bold" testId={`${testIdPrefix}-bold`} bold />
          <ToolbarBtn editor={editor} cmd="toggleItalic" label="I" active="italic" testId={`${testIdPrefix}-italic`} italic />
          <ToolbarBtn editor={editor} cmd="toggleStrike" label="S̶" active="strike" testId={`${testIdPrefix}-strike`} />
          <Sep />
          <ToolbarBtn editor={editor} cmd="toggleHeading" cmdArg={{ level: 2 }} label="H2" active="heading" activeArg={{ level: 2 }} testId={`${testIdPrefix}-h2`} />
          <ToolbarBtn editor={editor} cmd="toggleHeading" cmdArg={{ level: 3 }} label="H3" active="heading" activeArg={{ level: 3 }} testId={`${testIdPrefix}-h3`} />
          <Sep />
          <ToolbarBtn editor={editor} cmd="toggleBulletList" label="• List" active="bulletList" testId={`${testIdPrefix}-bullet`} />
          <ToolbarBtn editor={editor} cmd="toggleOrderedList" label="1. List" active="orderedList" testId={`${testIdPrefix}-ordered`} />
          <ToolbarBtn editor={editor} cmd="toggleBlockquote" label="❝" active="blockquote" testId={`${testIdPrefix}-quote`} />
          <Sep />
          <button
            type="button"
            data-testid={`${testIdPrefix}-link`}
            onClick={() => {
              const url = window.prompt('Link URL?');
              if (url === null) return;
              if (url === '') {
                editor.chain().focus().unsetLink().run();
              } else {
                editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
              }
            }}
            className="px-2 py-1 rounded hover:bg-gray-200"
          >🔗 Link</button>
          <button
            type="button"
            data-testid={`${testIdPrefix}-image`}
            onClick={() => {
              const url = window.prompt('Image URL?');
              if (url) editor.chain().focus().setImage({ src: url }).run();
            }}
            className="px-2 py-1 rounded hover:bg-gray-200"
          >🖼 Image</button>
          <Sep />
          <button
            type="button"
            data-testid={`${testIdPrefix}-undo`}
            onClick={() => editor.chain().focus().undo().run()}
            className="px-2 py-1 rounded hover:bg-gray-200"
          >↶</button>
          <button
            type="button"
            data-testid={`${testIdPrefix}-redo`}
            onClick={() => editor.chain().focus().redo().run()}
            className="px-2 py-1 rounded hover:bg-gray-200"
          >↷</button>
        </div>
      )}

      {/* Content surface */}
      {mode === 'visual' && (
        <div className="border border-t-0 rounded-b min-h-[300px] max-h-[60vh] overflow-y-auto p-3 prose prose-sm max-w-none"
             data-testid={`${testIdPrefix}-visual`}>
          <EditorContent editor={editor} />
        </div>
      )}

      {mode === 'source' && (
        <textarea
          data-testid={`${testIdPrefix}-source`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={18}
          className="w-full border rounded p-3 font-mono text-xs"
        />
      )}

      {mode === 'read' && (
        <div className="border rounded p-4 bg-gray-50 max-h-[60vh] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed"
             data-testid={`${testIdPrefix}-read`}>
          {stripHtml(value)}
        </div>
      )}

      {mode === 'read' && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
             data-testid={`${testIdPrefix}-read-notice`}>
          Read-only mode. Switch to <strong>Visual</strong> or <strong>Source</strong> to make changes.
        </div>
      )}

      {/* Shortcode / block-marker safety hint */}
      {mode !== 'read' && /\[[a-z0-9_-]+(?:\s|\])|<!--\s*wp:/i.test(value || '') && (
        <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded p-2"
             data-testid={`${testIdPrefix}-shortcode-hint`}>
          This page contains shortcodes or Gutenberg blocks (text in square brackets <code>[…]</code>
          or HTML comments <code>&lt;!-- wp:… --&gt;</code>). Leave them alone — they render
          dynamic content on the live site.
        </div>
      )}
    </div>
  );
}

function ToolbarBtn({ editor, cmd, cmdArg, label, active, activeArg, testId, bold, italic }) {
  const isActive = active && (activeArg ? editor.isActive(active, activeArg) : editor.isActive(active));
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => {
        const chain = editor.chain().focus();
        cmdArg ? chain[cmd](cmdArg).run() : chain[cmd]().run();
      }}
      className={`px-2 py-1 rounded hover:bg-gray-200 ${isActive ? 'bg-gray-300' : ''} ${bold ? 'font-bold' : ''} ${italic ? 'italic' : ''}`}
    >{label}</button>
  );
}

function Sep() {
  return <span className="w-px h-4 bg-gray-300 mx-1" />;
}
